/**
 * Plan my day: ONE proposes a realistic day — your meetings, the free time between them, and the tasks that deserve
 * today, pre-ticked until the free time is used. Untick, change minutes, decide what happens to overdue tasks you
 * didn't pick, and save. Today then shows the plan in order with times. The timeline beside the list shows meetings
 * and tasks by the hour: drag a task to a time (or type one) to pin it; the rest ONE fits around meetings and pins.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CalendarCheck, CalendarDays, ChevronLeft, ChevronRight, GripVertical, Lock, Wand2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, post, put } from "../lib/api";
import { Card, Empty, ErrorNote, fmtDate, PageHeader } from "../components/ui";
import { CategoryFilter, CategoryPill, matchCategory, type CategoryRef } from "../components/Categories";
import { SubProgress } from "../components/work";
import { finishWizard, PlanSteps } from "../components/plan/PlanSteps";
import { MoveTask, NextStepButton } from "../components/plan/TaskSteps";
import { DayTimeline, DRAG_TYPE, dragState, maltaInstant, minOf, toHHMM, toMin, type Pick, type Slot } from "../components/plan/DayTimeline";

interface Candidate { id: string; ref: string; title: string; priority: string; dueAt: string | null; nextAction: string | null; personal: boolean; space: string; reason: string; reasonLabel: string; minutes: number; category?: CategoryRef | null; subtasks?: { total: number; done: number } | null }
interface Block { start: string; end: string }
interface PlanDay {
  date: string; workday: { start: string; end: string }; calendarConnected: boolean;
  meetings: Array<{ subject: string; start: string; end: string; isAllDay: boolean; location: string | null; webLink: string }>;
  free: Block[]; freeMinutes: number; candidates: Candidate[]; suggested: Array<{ itemId: string; minutes: number; at?: string }>; saved: boolean; overdueCount: number;
  outlook: { connected: boolean; canWrite: boolean; on: boolean };
}

const MINUTES = [10, 15, 30, 45, 60, 90, 120, 180, 240];
/** A day that ends at 00:00 ends at midnight (the server reads it the same way). */
const endOf = (t: string) => (t === "00:00" ? "23:59" : t);
const dur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`);

/** Same rule as the server: pinned tasks keep their time; the rest go, in order, into the first free gap they fit. In minutes of the day. */
function preview(picks: Pick[], free: Block[], zero: number): Slot[] {
  let slots = free.map((b) => ({ s: minOf(b.start, zero), e: minOf(b.end, zero) }));
  for (const p of picks.filter((x) => x.at)) {
    const s0 = toMin(p.at!), s1 = s0 + p.minutes;
    slots = slots.flatMap((b) => (s1 <= b.s || s0 >= b.e ? [b] : [...(s0 > b.s ? [{ s: b.s, e: s0 }] : []), ...(s1 < b.e ? [{ s: s1, e: b.e }] : [])]));
  }
  return picks.map((p) => {
    if (p.at) return { ...p, start: toMin(p.at), pinned: true };
    const s = slots.find((b) => b.e - b.s >= p.minutes);
    if (!s) return { ...p, start: null, pinned: false };
    const start = s.s;
    s.s += p.minutes;
    return { ...p, start, pinned: false };
  });
}

export function PlanPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  // the day being planned: today by default; ‹ › walk the working days so a whole week can be planned (?date= keeps it on reload)
  const [date, setDate] = useState<string | null>(() => { try { return new URLSearchParams(location.search).get("date"); } catch { return null; } });
  const q = useQuery({ queryKey: ["plan-day", date ?? "today"], queryFn: () => api<PlanDay>(`/api/plan/day${date ? `?date=${date}` : ""}`) });
  const [picks, setPicks] = useState<Pick[]>([]);
  const [overdue, setOverdue] = useState<"leave" | "tomorrow" | "next_week">("leave");
  const [hours, setHours] = useState<{ start: string; end: string } | null>(null);
  const [toOutlook, setToOutlook] = useState(false);
  const [cat, setCat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [edits, setEdits] = useState(0); // bumped by every change you make; each one is saved by itself (below)
  const [autoSaved, setAutoSaved] = useState<"saving" | "saved" | null>(null);
  const d = q.data;
  const todayStr = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Malta" });
  const isToday = !d || d.date === todayStr;
  const stepDay = (from: string, by: 1 | -1) => {
    const x = new Date(`${from}T12:00:00Z`);
    do x.setUTCDate(x.getUTCDate() + by); while ([0, 6].includes(x.getUTCDay()));
    return x.toISOString().slice(0, 10);
  };
  const goDay = (next: string | null) => {
    const v = next && next > todayStr ? next : null; // never before today
    setDate(v);
    setEdits(0);
    setAutoSaved(null);
    try { const u = new URL(location.href); if (v) u.searchParams.set("date", v); else u.searchParams.delete("date"); history.replaceState(history.state, "", u); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!d) return;
    // times you set yourself come back as `at`; the rest are fitted again by the server
    setPicks(d.suggested.map((s) => ({ ...s, at: s.at ?? null })));
    setHours(d.workday);
    setToOutlook(d.outlook.on);
  }, [d]);

  const byId = useMemo(() => new Map((d?.candidates ?? []).map((c) => [c.id, c])), [d]);
  const picked = new Map(picks.map((p) => [p.itemId, p]));
  const planned = picks.reduce((n, p) => n + p.minutes, 0);
  const zero = useMemo(() => (d ? maltaInstant(d.date) : 0), [d]);
  const sched = useMemo(() => preview(picks, d?.free ?? [], zero), [picks, d, zero]);
  const unscheduled = sched.filter((s) => !s.start).length;
  const overdueLeft = (d?.candidates ?? []).filter((c) => c.reason === "overdue" && !picked.has(c.id)).length;

  const edit = <A extends unknown[]>(f: (...a: A) => void) => (...a: A) => { f(...a); setEdits((n) => n + 1); };
  const toggle = edit((c: Candidate) => setPicks((cur) => (picked.has(c.id) ? cur.filter((p) => p.itemId !== c.id) : [...cur, { itemId: c.id, minutes: c.minutes, at: null }])));
  const setAt = edit((id: string, at: string | null) => setPicks((cur) => {
    if (cur.some((p) => p.itemId === id)) return cur.map((p) => (p.itemId === id ? { ...p, at } : p));
    const c = byId.get(id);
    return c ? [...cur, { itemId: id, minutes: c.minutes, at }] : cur; // dragged straight from the list onto the timeline
  }));
  const setMin = edit((id: string, minutes: number) => setPicks((cur) => cur.map((p) => (p.itemId === id ? { ...p, minutes } : p))));
  const move = edit((id: string, dir: -1 | 1) => setPicks((cur) => {
    const i = cur.findIndex((p) => p.itemId === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= cur.length) return cur;
    const next = [...cur];
    [next[i], next[j]] = [next[j]!, next[i]!];
    return next;
  }));

  /** Drag a row onto another row in the list: it goes just above it (and is planned, if it wasn't). The order is the order ONE fits them in. */
  const [dropAt, setDropAt] = useState<string | null>(null);
  const placeBefore = edit((id: string, target: string) => setPicks((cur) => {
    if (id === target) return cur;
    const mine = cur.find((p) => p.itemId === id) ?? (() => { const c = byId.get(id); return c ? { itemId: id, minutes: c.minutes, at: null } : null; })();
    if (!mine) return cur;
    const rest = cur.filter((p) => p.itemId !== id);
    const at = rest.findIndex((p) => p.itemId === target);
    if (at < 0) return [...rest, mine];
    return [...rest.slice(0, at), mine, ...rest.slice(at)];
  }));

  /** Moved to another day: off today's list and plan (the save below keeps the plan without it). */
  const dropFromToday = (id: string) => { setPicks((cur) => cur.filter((p) => p.itemId !== id)); setEdits((n) => n + 1); void qc.invalidateQueries({ queryKey: ["plan-day"] }); };

  // every change is saved at once (a second after the last one), so leaving the page never loses a time you set;
  // "Plan N tasks" still finishes the day (overdue choice) and goes to Today
  useEffect(() => {
    if (!edits || !d) return;
    const t = setTimeout(async () => {
      setAutoSaved("saving");
      try {
        await post("/api/plan/day", { date: d.date, items: picks.map((p) => ({ itemId: p.itemId, minutes: p.minutes, ...(p.at ? { at: p.at } : {}) })), overdueAction: "leave",
          workday: hours && hours.start < endOf(hours.end) ? { start: hours.start, end: endOf(hours.end) } : undefined, outlook: d.outlook.canWrite ? toOutlook : undefined });
        setAutoSaved("saved");
        void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== "plan-day" }); // keep this page as you left it
      } catch (e) {
        setAutoSaved(null);
        setError(e);
      }
    }, 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits]);

  /** Save the plan. fit: forget every pinned time, let the server place everything, and stay on the page to see it. */
  const save = async (fit = false) => {
    setBusy(true);
    setError(null);
    try {
      const items = picks.map((p) => ({ itemId: p.itemId, minutes: p.minutes, ...(p.at && !fit ? { at: p.at } : {}) }));
      const r = await post<{ items: Array<{ itemId: string; minutes: number; start: string | null }>; outlook?: { error?: string } }>("/api/plan/day", {
        date: d?.date, items, overdueAction: overdueLeft && !fit && isToday ? overdue : "leave", workday: hours ? { start: hours.start, end: endOf(hours.end) } : undefined, outlook: d?.outlook.canWrite ? toOutlook : undefined,
      });
      if (fit && d) {
        // render from the server's answer at once; the refetch below brings the same times back
        setPicks(r.items.map((x) => ({ itemId: x.itemId, minutes: x.minutes, at: x.start ? toHHMM(minOf(x.start, zero)) : null })));
      }
      void qc.invalidateQueries();
      if (r.outlook?.error) setError(new Error(`Your plan is saved, but Outlook said: ${r.outlook.error}`));
      else if (!fit && !isToday) setAutoSaved("saved"); // another day: stay, so the next day can be planned
      else if (!fit) { finishWizard(); void navigate({ to: "/" }); }
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const changeHours = async (h: { start: string; end: string }) => {
    setHours(h);
    if (h.start >= endOf(h.end)) return;
    try {
      await put("/api/plan/hours", { ...h, end: endOf(h.end), ...(d ? { date: d.date } : {}) }); // remembers the hours; the free time is recomputed
      void qc.invalidateQueries({ queryKey: ["plan-day"] });
    } catch (e) {
      setError(e);
    }
  };

  if (q.isLoading || !d) return <Empty>{q.isLoading ? "Looking at your day…" : "Couldn't load your day."}</Empty>;

  const groups = new Map<string, Candidate[]>();
  for (const c of d.candidates.filter(matchCategory(cat))) groups.set(c.reasonLabel, [...(groups.get(c.reasonLabel) ?? []), c]);
  // within a group, picked tasks show in your order (the order ONE fits them in); the rest follow as ONE ranked them
  const pickIndex = new Map(picks.map((p, i) => [p.itemId, i]));
  for (const [k, cs] of groups) groups.set(k, [...cs].sort((a, b) => (pickIndex.get(a.id) ?? 1e6 + cs.indexOf(a)) - (pickIndex.get(b.id) ?? 1e6 + cs.indexOf(b))));
  const over = planned > d.freeMinutes && d.calendarConnected;

  return (
    <div className="space-y-5">
      <PlanSteps current="day" />
      <PageHeader
        eyebrow={
          <span className="flex items-center gap-1.5">
            <button type="button" className="btn btn-ghost btn-sm !px-1.5" disabled={isToday} onClick={() => goDay(stepDay(d.date, -1))} aria-label="Previous working day" title="Previous working day"><ChevronLeft size={15} /></button>
            <span>{isToday ? "Today · " : ""}{new Date(`${d.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</span>
            <button type="button" className="btn btn-ghost btn-sm !px-1.5" onClick={() => goDay(stepDay(d.date, 1))} aria-label="Next working day" title="Next working day"><ChevronRight size={15} /></button>
            {!isToday && <button type="button" className="btn btn-ghost btn-sm text-brand-700" onClick={() => goDay(null)}>Back to today</button>}
          </span>
        }
        title="Plan my day"
        subtitle="Pick what you'll really do today. ONE fits it around your meetings."
        actions={hours && (
          <span className="flex items-center gap-1.5 text-sm">
            <span className="muted">Working</span>
            <input type="time" className="input !w-auto !py-1" value={hours.start} onChange={(e) => void changeHours({ ...hours, start: e.target.value })} aria-label="Day starts" />
            <span className="muted">–</span>
            <input type="time" className="input !w-auto !py-1" value={hours.end} onChange={(e) => void changeHours({ ...hours, end: e.target.value })} aria-label="Day ends" />
          </span>
        )}
      />

      <div className="grid gap-5 lg:grid-cols-5">
        <div className="space-y-5 lg:col-span-3">
          <CategoryFilter items={d.candidates} value={cat} onChange={setCat} />
          {d.candidates.length === 0 && <Card><Empty>Nothing urgent, overdue or marked Next. Pick from your tasks, or enjoy the space.</Empty></Card>}
          {[...groups.entries()].map(([label, cs]) => (
            <Card key={label} title={isToday ? label : label.replace(/for today/i, `for ${new Date(`${d.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long" })}`)} count={cs.length} icon={label === "Overdue" ? <AlertTriangle size={16} /> : undefined} flush>
              <ul className="px-2 pb-1">
                {cs.map((c) => {
                  const p = picked.get(c.id);
                  return (
                    <li
                      key={c.id}
                      draggable
                      onDragStart={(e) => { dragState.current = { id: c.id, grab: 0, minutes: p?.minutes ?? c.minutes }; e.dataTransfer.setData(DRAG_TYPE, c.id); e.dataTransfer.setData("text/plain", c.title); e.dataTransfer.effectAllowed = "move"; }}
                      onDragEnd={() => { dragState.current = null; setDropAt(null); }}
                      onDragOver={(e) => { if (dragState.current && dragState.current.id !== c.id) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropAt !== c.id) setDropAt(c.id); } }}
                      onDragLeave={() => { if (dropAt === c.id) setDropAt(null); }}
                      onDrop={(e) => { e.preventDefault(); const id = dragState.current?.id; dragState.current = null; setDropAt(null); if (id) placeBefore(id, c.id); }}
                      className={`flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg px-3 py-2 hover:bg-black/[0.03] sm:flex-nowrap ${dropAt === c.id ? "border-t-2 border-brand-600" : ""}`}
                    >
                      <GripVertical size={14} className="muted -mx-1.5 hidden shrink-0 cursor-grab lg:block" aria-hidden />
                      <input type="checkbox" className="size-4 shrink-0" checked={!!p} onChange={() => toggle(c)} aria-label={`Plan ${c.title}`} />
                      <div className="min-w-0 flex-1 basis-[calc(100%-2.5rem)] sm:basis-auto">
                        <span className="flex items-center gap-1.5"><Link to="/work/$id" params={{ id: c.id }} target="_blank" rel="noopener" draggable={false} className="min-w-0 truncate text-sm font-medium hover:underline">{c.title}</Link>{c.category && <CategoryPill c={c.category} className="shrink-0" />}<SubProgress s={c.subtasks} /></span>
                        <span className="muted block truncate text-xs">
                          {c.nextAction ? `Next: ${c.nextAction}` : c.space}
                          {c.dueAt && <> · {c.reason === "overdue" ? <span className="font-semibold text-red-600">due {fmtDate(c.dueAt)}</span> : `due ${fmtDate(c.dueAt)}`}</>}
                        </span>
                      </div>
                      <span className="ml-7 flex shrink-0 items-center gap-1.5 sm:ml-0">
                      {c.personal && <Lock size={12} className="muted shrink-0" aria-label="Private" />}
                      {c.priority === "P1" ? <span className="chip shrink-0 text-xs text-red-700">Urgent</span> : c.priority === "P2" ? <span className="chip shrink-0 text-xs">High</span> : null}
                      <input
                        type="time" step={900} className="input !w-[6.5rem] shrink-0 !py-1 text-xs" disabled={!p} value={p?.at ?? ""}
                        onChange={(e) => setAt(c.id, e.target.value || null)}
                        aria-label={`Start time for ${c.title} (empty: ONE fits it)`} title="Set a start time, or leave empty and ONE fits it in"
                      />
                      <select className="input !w-auto shrink-0 !py-1 text-xs" disabled={!p} value={p?.minutes ?? c.minutes} onChange={(e) => setMin(c.id, Number(e.target.value))} aria-label="Minutes">
                        {[...new Set([...MINUTES, p?.minutes ?? c.minutes])].sort((a, b) => a - b).map((m) => <option key={m} value={m}>{dur(m)}</option>)}
                      </select>
                      <NextStepButton id={c.id} title={c.title} current={c.nextAction} className="btn btn-sm shrink-0 !px-2" onDone={(r) => { if (r.date && r.date !== d.date) dropFromToday(c.id); }} />
                      <MoveTask id={c.id} title={c.title} compact onMoved={(a) => { if (a !== "today") dropFromToday(c.id); }} />
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ))}
        </div>

        <div className="space-y-5 lg:col-span-2">
          <Card
            title="Your day"
            icon={<CalendarCheck size={16} />}
            actions={picks.length > 0 && <button className="btn btn-sm" disabled={busy} onClick={() => void save(true)} title="Clear every set time and let ONE place all tasks around your meetings, then save"><Wand2 size={14} /> Fit around my meetings</button>}
          >
            <div className="space-y-3">
              <div>
                <div className="flex items-baseline justify-between text-sm">
                  <span><b>{dur(planned)}</b> planned</span>
                  <span className="muted">{d.calendarConnected ? `${dur(d.freeMinutes)} free after meetings` : "Calendar not connected"}</span>
                </div>
                {d.calendarConnected && (
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-black/[0.06]">
                    <div className={`h-full rounded-full ${over ? "bg-red-500" : "bg-brand-500"}`} style={{ width: `${Math.min(100, d.freeMinutes ? (planned / d.freeMinutes) * 100 : 100)}%` }} />
                  </div>
                )}
                {over && <p className="mt-1.5 text-xs text-red-700">That's more than your free time — untick something or shorten it.</p>}
                {!d.calendarConnected && <p className="muted mt-1.5 text-xs">Connect Microsoft 365 in Settings and ONE will fit tasks around your meetings.</p>}
              </div>

              {d.candidates.length > 0 && <p className="muted text-xs">Drag a task onto a time, or set its time in the list. Tasks without a time are fitted around your meetings.</p>}
              {hours && (
                <DayTimeline
                  date={d.date}
                  zero={zero}
                  workday={hours.start < endOf(hours.end) ? { start: hours.start, end: endOf(hours.end) } : d.workday}
                  meetings={d.meetings}
                  slots={sched}
                  titleOf={(id) => byId.get(id)?.title ?? ""}
                  onPlace={(id, min) => setAt(id, toHHMM(min))}
                  onUnpin={(id) => setAt(id, null)}
                  onResize={setMin}
                  onMove={move}
                />
              )}
              {unscheduled > 0 && <p className="text-xs text-amber-700">{unscheduled} task{unscheduled === 1 ? "" : "s"} won't fit in today's free time — {unscheduled === 1 ? "it stays" : "they stay"} planned for today, without a time.</p>}
              {d.meetings.some((m) => m.isAllDay) && <p className="muted text-xs"><CalendarDays size={12} className="mr-1 inline" />All day: {d.meetings.filter((m) => m.isAllDay).map((m) => m.subject).join(", ")}</p>}
            </div>
          </Card>

          {overdueLeft > 0 && isToday && (
            <Card title={`Overdue you didn't pick (${overdueLeft})`} icon={<AlertTriangle size={16} />}>
              <div className="space-y-1.5 text-sm">
                {([["leave", "Leave them as they are"], ["tomorrow", "Move them to tomorrow"], ["next_week", "Move them to next Monday"]] as const).map(([v, label]) => (
                  <label key={v} className="flex items-center gap-2"><input type="radio" name="overdue" checked={overdue === v} onChange={() => setOverdue(v)} /> {label}</label>
                ))}
              </div>
            </Card>
          )}

          <div className="rounded-xl border border-[var(--line)] bg-[var(--panel)] px-4 py-3 text-sm">
            {d.outlook.canWrite ? (
              <label className="flex items-start gap-2">
                <input type="checkbox" className="mt-0.5 size-4" checked={toOutlook} onChange={(e) => setToOutlook(e.target.checked)} />
                <span><b>Put my plan in Outlook</b><span className="muted block text-xs">Each planned task becomes a time block in your calendar (“ONE · …”). Re-planning moves or removes them.</span></span>
              </label>
            ) : (
              <div className="flex items-center gap-3">
                <CalendarCheck size={16} className="muted shrink-0" />
                <span className="min-w-0 flex-1 text-xs">{d.outlook.connected ? "Want your plan as time blocks in Outlook? Allow ONE to add them to your calendar (once)." : "Connect Microsoft 365 to see your meetings here and put your plan in Outlook."}</span>
                <a className="btn btn-sm shrink-0" href="/api/personal/microsoft/connect?calendar=1">{d.outlook.connected ? "Allow" : "Connect"}</a>
              </div>
            )}
          </div>
          <ErrorNote error={error} />
          {autoSaved && <p className="muted text-xs">{autoSaved === "saving" ? "Saving…" : "Saved — your changes are kept even if you leave this page."}</p>}
          <div className="flex gap-2">
            <button className="btn btn-primary flex-1" disabled={busy} onClick={() => void save()}>{busy ? "Saving…" : `Plan ${picks.length} task${picks.length === 1 ? "" : "s"} for ${isToday ? "today" : new Date(`${d.date}T12:00:00`).toLocaleDateString("en-GB", { weekday: "long" })}`}</button>
            <Link to="/" className="btn">{autoSaved ? "Close" : "Cancel"}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
